import { useContext } from 'react'
import { AuthContext } from '@/context/auth-context'
import { useMatterBannerDismissal } from '@/matter/hooks/useMatterBannerDismissal'
import { MatterBannerKind } from '@/matter/store/schemas'
import { selectActiveMatter, selectIsMatterShared, selectMatterMembersInitialLoadComplete } from '@/matter/store/selectors'
import { canEditMatterMembers } from '@/matter/utils/matter-permissions'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'

/**
 * Decides whether the matter overview banner shows its collaboration prompt. Drive-sync prompts
 * live in the context rail, which sits directly above this banner, so the banner never competes
 * with them.
 */
export function useMatterOverviewBanner() {
  const authContext = useContext(AuthContext)

  const matter = useAppSelector(selectActiveMatter)
  const isMatterShared = useAppSelector((s) => selectIsMatterShared(s, matter?.id))
  const membersLoaded = useAppSelector((s) => selectMatterMembersInitialLoadComplete(s, matter?.id))
  const orgDirectoryUsers = useAppSelector((s: RootState) => s.accountState.orgDirectoryUsers)
  const { dismissedBanners, dismissedBannersLoaded } = useMatterBannerDismissal(matter?.id)

  const isOrgAccount = !!authContext?.userAccountData?.isOrgAccount
  const currentUid = authContext?.user?.uid
  const hasOtherOrgMembers = orgDirectoryUsers.some((u) => u.user_id !== currentUid)
  const orgMatterCanBeShared = isOrgAccount && hasOtherOrgMembers && membersLoaded && !isMatterShared
  const canShareMatter = (!isOrgAccount || orgMatterCanBeShared) && canEditMatterMembers(matter)

  const collaborationDismissed = dismissedBanners.includes(MatterBannerKind.Collaboration)
  const showCollaborationBanner = dismissedBannersLoaded && canShareMatter && !collaborationDismissed

  return { showCollaborationBanner, matter, isOrgAccount }
}
